import React from 'react'
import { FormattedMessage } from 'react-intl'
import { getUrlFromPath } from 'utilities/url'
import { accentuate } from '../'
import { Section, IconMessage, Heading } from '../s'
import Paragraph from 'components/typography/paragraph'
import Link from 'components/link'
import { OrderedList, ListItem } from 'components/list'
import {
    CheckCircleFilled as Success,
    AlertTriangleFilled as Warning
} from 'components/feather'

const TripletexSupportedFeatures = ({ type, name }) => (
    <Section>
        <Heading>
            <FormattedMessage
                id="integration_heading_supported_features"
                defaultMessage="Supported features" />
        </Heading>
        <OrderedList>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_tripletex_feature_heading_sync_users"
                                    defaultMessage="Synchronize users between Tripletex and Huma" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_tripletex_feature_text_sync_users"
                                defaultMessage="When synchronizing users manually, Huma will compare the users in Tripletex with users in your Huma organization. Decide what to do with non-matching and missing values, as well as with users that don’t exist in both systems." />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_tripletex_limitation_admin_email_address_sync_unavailable"
                                                defaultMessage="For <accent>users with system access</accent> in Tripletex, email address changes in Huma will not be reflected in Tripletex. Tripletex requires that email addresses are changed manually in their system for security reasons."
                                                values={{ accent: accentuate }} />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_tripletex_feature_heading_fields_in_sync"
                                    defaultMessage="Keep profiles automatically updated from Huma to Tripletex in real-time" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_tripletex_feature_text_fields_in_sync"
                                defaultMessage="Changes made in the supported profile fields (<link>listed below</link>) will be automatically updated in Tripletex."
                                values={{ link: chunks => (
                                    <Link to={getUrlFromPath('path:integrations.integration.details')({ type })}>
                                        {chunks}
                                    </Link>
                                ) }} />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_tripletex_feature_text_resync_updates"
                                                defaultMessage="Note that changes made in Tripletex will not be automatically updated in Huma. To do that, you’ll need to manually <accent>synchronize</accent> once again."
                                                values={{ accent: accentuate }} />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_label_createNewSalariesInIntegration"
                                    defaultMessage="Update salary data in {service} when updated in Huma"
                                    values={{ service: name }} />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_tripletex_disclaimer_salary_transfer_info"
                                defaultMessage="All newly created or updated salary data in Huma will be automatically transferred to Tripletex. If a salary’s start date does not match an existing position in Tripletex, a new position will be created." />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_tripletex_disclaimer_salary_transfer_direction"
                                                defaultMessage="Salary data will only be sent from Huma to Tripletex." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_tripletex_disclaimer_salary_transfer_limitation"
                                                defaultMessage="Only salaries created or updated after the integration is activated will be transferred to Tripletex." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
            <ListItem>
                <IconMessage className="success">
                    <Success size={20} />
                    <div>
                        <Paragraph>
                            <strong>
                                <FormattedMessage
                                    id="integration_tripletex_feature_heading_send_absence_data"
                                    defaultMessage="Send absence from Huma to Tripletex" />
                            </strong>
                            <br />
                            <FormattedMessage
                                id="integration_tripletex_feature_text_send_absence_data"
                                defaultMessage="Registered and approved absence from Huma can be transferred to Tripletex and assigned corresponding absence types there. This is configured in the <accent>Map absence types</accent> section under integration settings."
                                values={{ accent: accentuate }} />
                        </Paragraph>
                        <OrderedList>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph>
                                            <FormattedMessage
                                                id="integration_tripletex_feature_text_absence_changes"
                                                defaultMessage="Changes in absence in Huma will result in changes to the absence created in Tripletex." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                            <ListItem>
                                <IconMessage className="warning">
                                    <Warning size={20} />
                                    <div>
                                        <Paragraph className="compact">
                                            <FormattedMessage
                                                id="integration_tripletex_feature_text_absence_deletion"
                                                defaultMessage="If absence is deleted in Huma, it will also be deleted in Tripletex." />
                                        </Paragraph>
                                    </div>
                                </IconMessage>
                            </ListItem>
                        </OrderedList>
                    </div>
                </IconMessage>
            </ListItem>
        </OrderedList>
    </Section>
)

export default TripletexSupportedFeatures